import { useState, useEffect, useCallback } from 'react';
import {
  FiX,
  FiTrendingUp,
  FiTrendingDown,
  FiPercent,
  FiPieChart,
  FiActivity,
  FiRefreshCw,
  FiClock,
} from 'react-icons/fi';
import { api } from '../../services/api';
import { useI18n } from '../../hooks/useI18n';
import { formatLocalDateTime } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';

export function PortfolioModal({ open, onClose, pushToast }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('positions'); // positions | profit | history
  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState([]);
  const [profitSummary, setProfitSummary] = useState(null);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [sellPosition, setSellPosition] = useState(null);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [redeemPosition, setRedeemPosition] = useState(null);
  const [redeemAllLoading, setRedeemAllLoading] = useState(false);
  const [autoExitModalOpen, setAutoExitModalOpen] = useState(false);
  const [autoExitPosition, setAutoExitPosition] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, profitRes, historyRes] = await Promise.all([
        api.getPositions(),
        api.getProfitSummary(),
        api.getTradeHistory(20),
      ]);

      if (posRes.code === 'SUCCESS') {
        setPositions(posRes.data || []);
      }
      if (profitRes.code === 'SUCCESS') {
        setProfitSummary(profitRes.data);
      }
      if (historyRes.code === 'SUCCESS') {
        setTradeHistory(historyRes.data || []);
      }
    } catch (e) {
      console.error('加载数据失败', e);
      pushToast?.(t('portfolio.loadFailed'), 'error');
    }
    setLoading(false);
  }, [t, pushToast]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  if (!open) return null;

  const formatPnl = (value) => {
    const num = Number(value || 0);
    const sign = num >= 0 ? '+' : '';
    return `${sign}$${num.toFixed(2)}`;
  };

  const formatPercent = (value) => {
    const num = Number(value || 0);
    const sign = num >= 0 ? '+' : '';
    return `${sign}${num.toFixed(1)}%`;
  };

  const handleSellClick = (position) => {
    if (!position?.token_id) {
      pushToast?.(t('trading.tokenNotAvailable') || '该市场暂不支持交易', 'error');
      return;
    }
    setSellPosition(position);
    setSellModalOpen(true);
  };

  const handleSellClose = () => {
    setSellModalOpen(false);
    setSellPosition(null);
  };

  const handleRedeemClick = (position) => {
    if (!position?.redeemable) return;
    setRedeemPosition(position);
    setRedeemModalOpen(true);
  };

  const handleRedeemClose = () => {
    setRedeemModalOpen(false);
    setRedeemPosition(null);
  };

  const handleAutoExitClick = (position) => {
    const isPro = user?.tier_name === 'pro';
    if (!isPro) {
      window.dispatchEvent(
        new CustomEvent('membership-required', { detail: { currentTier: user?.tier_name || 'free' } })
      );
      pushToast?.(t('trading.proOnly'), 'warning');
      return;
    }
    if (!position?.token_id) {
      pushToast?.(t('trading.tokenNotAvailable') || '该市场暂不支持交易', 'error');
      return;
    }
    setAutoExitPosition(position);
    setAutoExitModalOpen(true);
  };

  const handleAutoExitClose = () => {
    setAutoExitModalOpen(false);
    setAutoExitPosition(null);
  };

  const handleRedeemAll = async () => {
    const redeemableCount = positions.filter(
      (pos) => pos.redeemable && pos.condition_id
    ).length;
    if (!redeemableCount) {
      pushToast?.(t('trading.redeemUnavailable'), 'warning');
      return;
    }
    if (!window.confirm(t('trading.confirmRedeemAll'))) {
      return;
    }

    setRedeemAllLoading(true);
    try {
      const res = await api.redeemAllPositions();
      if (res.code === 'SUCCESS') {
        pushToast?.(t('trading.redeemAllSuccess'), 'success');
        await loadData();
      } else {
        pushToast?.(res.msg || res.detail || t('trading.redeemAllFailed'), 'error');
      }
    } catch (e) {
      pushToast?.(e.message || t('trading.redeemAllFailed'), 'error');
    } finally {
      setRedeemAllLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-2xl max-h-[85vh] rounded-3xl border border-glass-border bg-surface/95 shadow-[0_30px_80px_rgba(0,0,0,0.45)] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-6 border-b border-glass-border shrink-0">
          <div className="flex items-center gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                {t('portfolio.myPortfolio')}
              </div>
              <div className="mt-1 text-xl font-semibold text-white flex items-center gap-2">
                {t('portfolio.title')}
                <button
                  onClick={loadData}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-glass-hover transition-all"
                  title={t('wallet.refresh')}
                >
                  <FiRefreshCw size={16} />
                </button>
              </div>
            </div>
          </div>
          <button
            className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white hover:bg-glass-hover transition-colors"
            onClick={onClose}
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-glass-border">
          {[
            { key: 'positions', icon: FiPieChart, label: t('portfolio.positions') },
            { key: 'profit', icon: FiActivity, label: t('portfolio.profit') },
            { key: 'history', icon: FiClock, label: t('portfolio.history') },
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === key
                  ? 'bg-surface-highlight text-white border-b-2 border-accent'
                  : 'text-slate-400 hover:text-white hover:bg-surface-highlight/50'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <div className="w-8 h-8 border-3 border-accent/30 border-t-accent rounded-full animate-spin mb-3"></div>
              <p className="text-sm">{t('common.loading')}</p>
            </div>
          ) : activeTab === 'positions' ? (
            <PositionsTab
              positions={positions}
              t={t}
              onSell={handleSellClick}
              onRedeem={handleRedeemClick}
              onAutoExit={handleAutoExitClick}
              onRedeemAll={handleRedeemAll}
              redeemAllLoading={redeemAllLoading}
            />
          ) : activeTab === 'profit' ? (
            <ProfitTab summary={profitSummary} t={t} formatPnl={formatPnl} formatPercent={formatPercent} />
          ) : (
            <HistoryTab history={tradeHistory} t={t} lang={lang} />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-glass-border shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-glass-hover transition-colors text-sm font-medium"
          >
            {t('wallet.close')}
          </button>
        </div>
      </div>

      <SellPositionModal
        open={sellModalOpen}
        position={sellPosition}
        onClose={handleSellClose}
        onSuccess={loadData}
        pushToast={pushToast}
        t={t}
      />

      <RedeemPositionModal
        open={redeemModalOpen}
        position={redeemPosition}
        onClose={handleRedeemClose}
        onSuccess={loadData}
        pushToast={pushToast}
        t={t}
      />

      <AutoExitModal
        open={autoExitModalOpen}
        position={autoExitPosition}
        onClose={handleAutoExitClose}
        onSuccess={loadData}
        pushToast={pushToast}
        t={t}
      />
    </div>
  );
}

function PositionsTab({ positions, t, onSell, onRedeem, onAutoExit, onRedeemAll, redeemAllLoading }) {
  if (positions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 rounded-2xl border-2 border-dashed border-glass-border">
        <div className="w-12 h-12 mb-3 rounded-full bg-surface-highlight flex items-center justify-center text-2xl">
          📊
        </div>
        <p className="text-slate-400 text-sm">{t('portfolio.noPositions')}</p>
      </div>
    );
  }

  const redeemableCount = positions.filter((pos) => pos.redeemable && pos.condition_id).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between px-1">
        <div className="text-xs text-slate-500">
          {t('trading.redeemableCount', { count: redeemableCount })}
        </div>
        <button
          type="button"
          onClick={() => onRedeemAll?.()}
          disabled={!redeemableCount || redeemAllLoading}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
            !redeemableCount || redeemAllLoading
              ? 'border-slate-500/30 text-slate-500 cursor-not-allowed'
              : 'border-positive/40 text-positive hover:bg-positive/10'
          }`}
        >
          {redeemAllLoading ? t('trading.redeemAllSubmitting') : t('trading.redeemAll')}
        </button>
      </div>
      {positions.map((pos, idx) => (
        <div
          key={pos.token_id || idx}
          className="p-4 rounded-2xl bg-surface-highlight/50 border border-glass-border hover:border-accent/30 transition-all"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-white truncate">{pos.title || pos.question || pos.outcome}</h4>
              <p className="text-xs text-slate-500 mt-0.5">{pos.outcome}</p>
            </div>
            <div className="text-right shrink-0">
              <div className={`text-sm font-bold ${pos.pnl >= 0 ? 'text-positive' : 'text-negative'}`}>
                {pos.pnl >= 0 ? '+' : ''}${pos.pnl?.toFixed(2)}
              </div>
              <div className={`text-xs ${pos.pnl_percent >= 0 ? 'text-positive/70' : 'text-negative/70'}`}>
                {pos.pnl_percent >= 0 ? '+' : ''}{pos.pnl_percent?.toFixed(1)}%
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-slate-500">{t('portfolio.size')}</span>
              <div className="text-white font-mono mt-0.5">{pos.size?.toFixed(2)}</div>
            </div>
            <div>
              <span className="text-slate-500">{t('portfolio.avgPrice')}</span>
              <div className="text-white font-mono mt-0.5">${pos.avg_price?.toFixed(4)}</div>
            </div>
            <div>
              <span className="text-slate-500">{t('portfolio.currentPrice')}</span>
              <div className="text-white font-mono mt-0.5">${pos.current_price?.toFixed(4)}</div>
            </div>
            <div>
              <span className="text-slate-500">{t('portfolio.value')}</span>
              <div className="text-white font-mono mt-0.5">${pos.current_value?.toFixed(2)}</div>
            </div>
          </div>

          {(pos.auto_exit_stop_loss || pos.auto_exit_take_profit) && (
            <div className="mt-3 text-[11px] text-slate-400 flex flex-wrap gap-3">
              {pos.auto_exit_stop_loss != null && (
                <span>{t('trading.stopLossLabel')}: ${Number(pos.auto_exit_stop_loss).toFixed(4)}</span>
              )}
              {pos.auto_exit_take_profit != null && (
                <span>{t('trading.takeProfitLabel')}: ${Number(pos.auto_exit_take_profit).toFixed(4)}</span>
              )}
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            {pos.redeemable ? (
              <button
                type="button"
                onClick={() => onRedeem?.(pos)}
                disabled={!pos.condition_id}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  !pos.condition_id
                    ? 'border-slate-500/30 text-slate-500 cursor-not-allowed'
                    : 'border-positive/40 text-positive hover:bg-positive/10'
                }`}
              >
                {t('trading.redeem')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => onAutoExit?.(pos)}
                  disabled={!pos.token_id || Number(pos.size || 0) <= 0}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    !pos.token_id || Number(pos.size || 0) <= 0
                      ? 'border-slate-500/30 text-slate-500 cursor-not-allowed'
                      : 'border-accent/40 text-accent hover:bg-accent/10'
                  }`}
                >
                  {t('trading.autoExit')}
                </button>
                <button
                  type="button"
                  onClick={() => onSell?.(pos)}
                  disabled={!pos.token_id || Number(pos.size || 0) <= 0}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    !pos.token_id || Number(pos.size || 0) <= 0
                      ? 'border-slate-500/30 text-slate-500 cursor-not-allowed'
                      : 'border-negative/40 text-negative hover:bg-negative/10'
                  }`}
                >
                  {t('trading.sell')}
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SellPositionModal({ open, position, onClose, onSuccess, pushToast, t }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && position) {
      setAmount(position.size != null ? String(position.size) : '');
    }
  }, [open, position]);

  if (!open || !position) return null;

  const maxSize = Number(position.size || 0);
  const amountNum = Number(amount || 0);
  const currentPrice = Number(position.current_price || 0);
  const estimatedValue = amount && currentPrice ? (amountNum * currentPrice).toFixed(2) : '0.00';

  const handleSell = async () => {
    if (!amount || amountNum <= 0) {
      pushToast?.(t('trading.enterAmount'), 'warning');
      return;
    }
    if (maxSize > 0 && amountNum > maxSize) {
      pushToast?.(t('trading.insufficientPosition'), 'error');
      return;
    }
    if (!position.token_id) {
      pushToast?.(t('trading.tokenNotAvailable') || '该市场暂不支持交易', 'error');
      return;
    }

    setLoading(true);
    try {
      const res = await api.placeOrder(position.token_id, 'SELL', amountNum);
      if (res.code === 'SUCCESS') {
        const data = res.data;
        const isSuccess = data.status === 'success' || data.status === 'partial';
        const isSubmitted = data.status === 'submitted';
        if (isSuccess) {
          pushToast?.(
            t('trading.orderSuccess', {
              filled: data.filled_size?.toFixed(2),
              price: data.avg_price?.toFixed(4),
            }),
            'success'
          );
          onSuccess?.();
          onClose();
        } else if (isSubmitted) {
          pushToast?.(t('trading.orderSubmitted'), 'info');
          onSuccess?.();
          onClose();
        } else {
          pushToast?.(data.message || t('trading.orderFailed'), 'error');
        }
      } else {
        pushToast?.(res.msg || t('trading.orderFailed'), 'error');
      }
    } catch (e) {
      pushToast?.(e.message || t('trading.requestFailed'), 'error');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div>
            <h3 className="text-lg font-bold text-white">{t('trading.sell')}</h3>
            <p className="text-sm text-gray-400 truncate max-w-[280px]">
              {position.title || position.question || position.outcome}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="space-y-2">
            <div className="flex justify-between">
              <label className="text-xs font-medium text-gray-400">{t('trading.amountShares')}</label>
              <span className="text-xs text-gray-500">
                {t('portfolio.size')}: {maxSize.toFixed(2)}
              </span>
            </div>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full bg-surface border border-glass-border rounded-xl pl-10 pr-16 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <FiPercent size={18} />
              </div>
              <button
                onClick={() => setAmount(maxSize.toString())}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-accent hover:text-accent-soft font-medium px-2 py-1 rounded hover:bg-accent/10 transition-colors"
              >
                {t('wallet.max')}
              </button>
            </div>
          </div>

          <div className="p-4 bg-surface rounded-xl border border-glass-border space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('trading.outcome')}</span>
              <span className="text-white font-medium">{position.outcome}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('trading.currentPrice')}</span>
              <span className="text-white font-mono">${currentPrice.toFixed(4)}</span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-glass-border/50">
              <span className="text-gray-500">{t('trading.potentialPayout')}</span>
              <span className="text-white font-bold">${estimatedValue}</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-glass-border flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors text-sm font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSell}
            disabled={loading || !amount}
            className="px-6 py-2 rounded-xl text-white text-sm font-medium shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-negative hover:bg-negative/80 shadow-negative/20"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiTrendingDown />
            )}
            {t('trading.confirmSell')}
          </button>
        </div>
      </div>
    </div>
  );
}

function RedeemPositionModal({ open, position, onClose, onSuccess, pushToast, t }) {
  const [loading, setLoading] = useState(false);

  if (!open || !position) return null;

  const redeemValue = Number(position.current_value || 0).toFixed(2);

  const handleRedeem = async () => {
    if (!position.condition_id) {
      pushToast?.(t('trading.redeemUnavailable'), 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await api.redeemPosition(position.condition_id);
      if (res.code === 'SUCCESS') {
        pushToast?.(t('trading.redeemSuccess'), 'success');
        onSuccess?.();
        onClose();
      } else {
        pushToast?.(res.msg || res.detail || t('trading.redeemFailed'), 'error');
      }
    } catch (e) {
      pushToast?.(e.message || t('trading.redeemFailed'), 'error');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div>
            <h3 className="text-lg font-bold text-white">{t('trading.redeem')}</h3>
            <p className="text-sm text-gray-400 truncate max-w-[280px]">
              {position.title || position.question || position.outcome}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 bg-surface rounded-xl border border-glass-border space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('trading.outcome')}</span>
              <span className="text-white font-medium">{position.outcome}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('portfolio.size')}</span>
              <span className="text-white font-mono">{position.size?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('trading.redeemAmount')}</span>
              <span className="text-white font-mono">${redeemValue}</span>
            </div>
          </div>
          <div className="text-xs text-gray-500">{t('trading.redeemNotice')}</div>
        </div>

        <div className="px-6 py-4 border-t border-glass-border flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors text-sm font-medium"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleRedeem}
            disabled={loading}
            className="px-6 py-2 rounded-xl text-white text-sm font-medium shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-positive hover:bg-positive/80 shadow-positive/20"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <FiTrendingUp />
            )}
            {t('trading.confirmRedeem')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AutoExitModal({ open, position, onClose, onSuccess, pushToast, t }) {
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && position) {
      setStopLoss(
        position.auto_exit_stop_loss != null ? String(position.auto_exit_stop_loss) : ''
      );
      setTakeProfit(
        position.auto_exit_take_profit != null ? String(position.auto_exit_take_profit) : ''
      );
    }
  }, [open, position]);

  if (!open || !position) return null;

  const currentPrice = Number(position.current_price || 0);
  const stopLossValue = stopLoss !== '' ? Number(stopLoss) : null;
  const takeProfitValue = takeProfit !== '' ? Number(takeProfit) : null;

  const getValidationError = () => {
    if (stopLossValue !== null) {
      if (Number.isNaN(stopLossValue) || stopLossValue <= 0 || stopLossValue >= 1) {
        return t('trading.invalidStopLoss');
      }
    }
    if (takeProfitValue !== null) {
      if (Number.isNaN(takeProfitValue) || takeProfitValue <= 0 || takeProfitValue >= 1) {
        return t('trading.invalidTakeProfit');
      }
    }
    if (stopLossValue !== null && takeProfitValue !== null && stopLossValue >= takeProfitValue) {
      return t('trading.invalidStopLossRange');
    }
    if (stopLossValue !== null && currentPrice && stopLossValue >= currentPrice) {
      return t('trading.stopLossBelowCurrent');
    }
    if (takeProfitValue !== null && currentPrice && takeProfitValue <= currentPrice) {
      return t('trading.takeProfitAboveCurrent');
    }
    return null;
  };

  const validationError = getValidationError();
  const canSave = !validationError;

  const validate = () => {
    if (validationError) {
      pushToast?.(validationError, 'warning');
      return false;
    }
    if (stopLossValue !== null) {
      if (Number.isNaN(stopLossValue) || stopLossValue <= 0 || stopLossValue >= 1) {
        pushToast?.(t('trading.invalidStopLoss'), 'warning');
        return false;
      }
    }
    if (takeProfitValue !== null) {
      if (Number.isNaN(takeProfitValue) || takeProfitValue <= 0 || takeProfitValue >= 1) {
        pushToast?.(t('trading.invalidTakeProfit'), 'warning');
        return false;
      }
    }
    if (stopLossValue !== null && takeProfitValue !== null && stopLossValue >= takeProfitValue) {
      pushToast?.(t('trading.invalidStopLossRange'), 'warning');
      return false;
    }
    if (stopLossValue !== null && currentPrice && stopLossValue >= currentPrice) {
      pushToast?.(t('trading.stopLossBelowCurrent'), 'warning');
      return false;
    }
    if (takeProfitValue !== null && currentPrice && takeProfitValue <= currentPrice) {
      pushToast?.(t('trading.takeProfitAboveCurrent'), 'warning');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        token_id: position.token_id,
        condition_id: position.condition_id,
        market_title: position.title || position.question || null,
        outcome: position.outcome,
        stop_loss_price: stopLossValue,
        take_profit_price: takeProfitValue,
        target_shares: Number(position.size || 0) || null,
      };
      const res = await api.setAutoExit(payload);
      if (res.code === 'SUCCESS') {
        pushToast?.(res.msg || t('common.saved'), 'success');
        onSuccess?.();
        onClose();
      } else {
        pushToast?.(res.msg || t('common.error'), 'error');
      }
    } catch (e) {
      if (e?.isMembershipRequired) {
        window.dispatchEvent(
          new CustomEvent('membership-required', { detail: { currentTier: e.currentTier || 'free' } })
        );
        pushToast?.(t('trading.proOnly'), 'warning');
      } else {
        pushToast?.(e.message || t('trading.requestFailed'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      const res = await api.setAutoExit({
        token_id: position.token_id,
        condition_id: position.condition_id,
        market_title: position.title || position.question || null,
        outcome: position.outcome,
        stop_loss_price: null,
        take_profit_price: null,
        target_shares: Number(position.size || 0) || null,
      });
      if (res.code === 'SUCCESS') {
        pushToast?.(res.msg || t('common.saved'), 'success');
        onSuccess?.();
        onClose();
      } else {
        pushToast?.(res.msg || t('common.error'), 'error');
      }
    } catch (e) {
      pushToast?.(e.message || t('trading.requestFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md bg-surface-highlight rounded-2xl border border-glass-border shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-glass-border">
          <div>
            <h3 className="text-lg font-bold text-white">{t('trading.autoExit')}</h3>
            <p className="text-sm text-gray-400 truncate max-w-[280px]">
              {position.title || position.question || position.outcome}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-surface-highlight transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-gray-400">{t('trading.stopLossLabel')}</label>
              <input
                type="number"
                step="0.0001"
                min="0.0001"
                max={currentPrice ? Math.max(0, currentPrice - 0.0001) : 0.9999}
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                placeholder="0.0000"
                className="w-full bg-surface border border-glass-border rounded-xl px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all text-sm"
              />
              <div className="text-[10px] text-gray-500">{t('trading.stopLossHint')}</div>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-gray-400">{t('trading.takeProfitLabel')}</label>
              <input
                type="number"
                step="0.0001"
                min={currentPrice ? Math.min(0.9999, currentPrice + 0.0001) : 0.0001}
                max="0.9999"
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
                placeholder="0.0000"
                className="w-full bg-surface border border-glass-border rounded-xl px-3 py-2 text-white placeholder-gray-600 focus:outline-none focus:border-accent/50 focus:ring-1 focus:ring-accent/50 transition-all text-sm"
              />
              <div className="text-[10px] text-gray-500">{t('trading.takeProfitHint')}</div>
            </div>
          </div>

          {validationError && (
            <div className="text-xs text-negative">{validationError}</div>
          )}

          <div className="p-3 rounded-xl bg-surface border border-glass-border text-xs text-slate-400">
            <div className="flex justify-between">
              <span>{t('trading.currentPrice')}</span>
              <span className="font-mono text-white">${currentPrice.toFixed(4)}</span>
            </div>
            <div className="flex justify-between mt-2">
              <span>{t('portfolio.size')}</span>
              <span className="font-mono text-white">{Number(position.size || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-glass-border flex justify-end gap-3">
          <button
            onClick={handleClear}
            disabled={saving}
            className="px-4 py-2 rounded-xl text-slate-300 hover:text-white hover:bg-glass-hover transition-colors text-sm font-medium"
          >
            {t('common.clear')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !canSave}
            className="px-5 py-2 rounded-xl text-white text-sm font-medium shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-accent hover:bg-accent/80 shadow-accent/20"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              t('common.save')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfitTab({ summary, t, formatPnl, formatPercent }) {
  if (!summary) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <p className="text-sm">{t('portfolio.noData')}</p>
      </div>
    );
  }

  const stats = [
    {
      label: t('portfolio.totalPnl'),
      value: formatPnl(summary.total_pnl),
      color: summary.total_pnl >= 0 ? 'text-positive' : 'text-negative',
      icon: summary.total_pnl >= 0 ? FiTrendingUp : FiTrendingDown,
    },
    {
      label: t('portfolio.realizedPnl'),
      value: formatPnl(summary.realized_pnl),
      color: summary.realized_pnl >= 0 ? 'text-positive' : 'text-negative',
    },
    {
      label: t('portfolio.unrealizedPnl'),
      value: formatPnl(summary.unrealized_pnl),
      color: summary.unrealized_pnl >= 0 ? 'text-positive' : 'text-negative',
    },
    {
      label: t('portfolio.totalInvested'),
      value: `$${summary.total_invested?.toFixed(2)}`,
      color: 'text-white',
    },
    {
      label: t('portfolio.currentValue'),
      value: `$${summary.current_value?.toFixed(2)}`,
      color: 'text-white',
    },
    {
      label: t('portfolio.winRate'),
      value: `${summary.win_rate?.toFixed(1)}%`,
      color: summary.win_rate >= 50 ? 'text-positive' : 'text-negative',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Main PnL Card */}
      <div className="p-6 rounded-2xl bg-gradient-to-br from-surface-highlight to-surface border border-glass-border">
        <div className="text-center">
          <p className="text-sm text-slate-400 mb-2">{t('portfolio.totalPnl')}</p>
          <div className={`text-4xl font-bold ${summary.total_pnl >= 0 ? 'text-positive' : 'text-negative'}`}>
            {formatPnl(summary.total_pnl)}
          </div>
          <div className="flex items-center justify-center gap-4 mt-4 text-sm">
            <span className="text-slate-400">
              {t('portfolio.totalTrades')}: <span className="text-white font-bold">{summary.total_trades}</span>
            </span>
            <span className="text-slate-400">
              {t('portfolio.winRate')}: <span className={summary.win_rate >= 50 ? 'text-positive' : 'text-negative'}>{summary.win_rate?.toFixed(1)}%</span>
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {stats.slice(1).map((stat, idx) => (
          <div key={idx} className="p-4 rounded-xl bg-surface-highlight/50 border border-glass-border">
            <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
            <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTab({ history, t, lang }) {
  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 rounded-2xl border-2 border-dashed border-glass-border">
        <div className="w-12 h-12 mb-3 rounded-full bg-surface-highlight flex items-center justify-center text-2xl">
          📜
        </div>
        <p className="text-slate-400 text-sm">{t('portfolio.noHistory')}</p>
      </div>
    );
  }

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return formatLocalDateTime(
      timestamp,
      {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      },
      lang
    );
  };

  return (
    <div className="space-y-2">
      {history.map((trade, idx) => {
        const isWin = trade.side === 'WIN';
        const sideLabel = isWin ? t('portfolio.win') : t('portfolio.loss');
        const pnl = trade.pnl || 0;
        const cost = trade.cost || 0;
        return (
          <div
            key={trade.id || idx}
            className="flex items-center justify-between p-3 rounded-xl bg-surface-highlight/50 border border-glass-border hover:border-accent/30 transition-all"
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isWin ? 'bg-positive/20 text-positive' : 'bg-negative/20 text-negative'
                }`}
              >
                {isWin ? <FiTrendingUp size={16} /> : <FiTrendingDown size={16} />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                    isWin ? 'bg-positive/20 text-positive' : 'bg-negative/20 text-negative'
                  }`}>
                    {sideLabel}
                  </span>
                  <p className="text-sm font-medium text-white truncate max-w-[180px]">{trade.title || trade.outcome || '-'}</p>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{formatTime(trade.timestamp)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-sm font-bold ${isWin ? 'text-positive' : 'text-negative'}`}>
                {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500">{t('portfolio.cost')}: ${cost.toFixed(2)}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
